import {
  Avatar,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popover,
  type SvgIconTypeMap,
  useTheme,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import type { FC, MouseEventHandler } from "react";
import { useState } from "react";

const accountSettingsPopoverIdentifier = "account-settings-popover";

export type PMHeaderAccountSettingsProps = {
  /** user display name */
  username?: string;
  /** user email */
  groups?: string[];
  /** renders entries as `ListItem`s */
  entries?: {
    /** the entry's `onClick` handler */
    onClick: MouseEventHandler<HTMLDivElement>;

    /** the entry's `icon`, should be a `@mui` icon */
    /* eslint-disable-next-line */
    icon: OverridableComponent<SvgIconTypeMap<any, "svg">> & {
      muiName: string;
    };
    /** the entry's displayed `text` */
    text: string;
  }[];
};
/**
 * Returns the concatenated uppercase initials of a given name string.
 *
 * @param {string} fullName - The full name (e.g., "Bob John").
 * @returns {string} The initials (e.g., "BJ").
 */
function getInitials(fullName: string) {
  if (typeof fullName !== "string") {
    throw new TypeError("Expected a string");
  }

  // Split on one or more whitespace characters, filter out empty parts
  const parts = fullName
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);

  // Map each part to its first character uppercased, then join
  const initials = parts.map((part) => part[0].toUpperCase()).join("");

  return initials;
}

export const PMHeaderAccountSettings: FC<PMHeaderAccountSettingsProps> = ({
  entries = [],
  username,
  groups = [],
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const accountSettingsOpen = Boolean(anchorEl);
  const theme = useTheme();
  function stringToColor(string: string) {
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
  }
  return (
    <Box data-testid="header-account-settings" display="inline">
      <Button
        aria-describedby="account-settings-popover"
        onClick={handleClick}
        data-testid="header-account-settings-button"
        sx={{
          borderColor: blue[50],
          borderRadius: theme.shape.borderRadius,
          padding: "7px 10px",
        }}
      >
        <Avatar
          sx={{
            backgroundColor: stringToColor(username ?? ""),
            width: 35,
            height: 35,
          }}
        >
          {username ? getInitials(username) : ""}
        </Avatar>
      </Button>
      <Popover
        id={accountSettingsPopoverIdentifier}
        open={accountSettingsOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        data-testid="header-account-settings-popover"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{ "@media print": { display: "none" } }}
      >
        <Box sx={{ maxWidth: 450 }}>
          <List>
            {username && (
              <>
                <ListSubheader sx={{ color: theme.palette.text.primary }}>
                  {username}
                </ListSubheader>
                {groups.length == 0 && <Divider />}
              </>
            )}
            {groups.length > 0 && (
              <>
                <ListSubheader>Groups: {groups.join(", ")}</ListSubheader>
                <Divider />
              </>
            )}
            {entries.map(({ icon: Icon, onClick, text }) => (
              <ListItem key={text}>
                <ListItemButton
                  onClick={(...args) => {
                    onClick(...args);
                  }}
                >
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};
